import React, { useContext } from "react";

// Try to import react-router-dom components
let RouterLink: React.ComponentType<any> | null = null,
  RouterContext: React.Context<any> | null = null;

try {
  // oxlint-disable-next-line typescript/ban-ts-comment, typescript/prefer-ts-expect-error -- require is untyped here but typed under the API's Node types, so @ts-expect-error fails one build
  // @ts-ignore - This might not exist
  const Router = require("react-router-dom");
  RouterLink = Router.Link;
  // React Router v6 uses UNSAFE_NavigationContext
  RouterContext = Router.UNSAFE_NavigationContext || Router.__RouterContext;
} catch {
  // React-router-dom not available - that's fine
}

/**
 * Get the ServicePro config, checking parent window first (for iframe context).
 */
const getConfig = (): any => {
    if (typeof window === "undefined") {
      return null;
    }
    try {
      if (window.parent && window.parent !== window && (window.parent as any).__SERVICEPRO_CONFIG__) {
        return (window.parent as any).__SERVICEPRO_CONFIG__;
      }
    } catch {
      /* Cross-origin */
    }
    return (window as any).__SERVICEPRO_CONFIG__ || null;
  },

  /**
   * In edit mode, try to navigate the editor to the page matching this URL.
   * Returns true if we handled it (caller should preventDefault), false otherwise.
   */
  handleEditorNavigation = (url: string): boolean => {
    const config = getConfig();
    if (!config?.isEditing) {
      return false;
    }

    const pages: { id: string; path: string }[] | undefined = config.pages;
    if (!pages?.length) {
      return true;
    } // Still editing, just block

    const normalize = (p: string) => {
      if (p === "/") {
        return "/";
      }
      return p.replace(/\/$/u, "");
    },
      match = pages.find((p) => normalize(p.path) === normalize(url));
    if (!match) {
      return true;
    } // No matching page, just block

    // Call the editor's navigate callback
    const navigate =
      window.parent === window
        ? (window as any).__SERVICEPRO_NAVIGATE__
        : (window.parent as any).__SERVICEPRO_NAVIGATE__;
    if (typeof navigate === "function") {
      navigate(match.id);
    }

    return true;
  },

  // Stand-in so useContext can be called unconditionally when react-router-dom
  // Isn't installed. RouterContext is resolved once at module load, so the
  // Context identity passed to useContext never changes between renders.
  NoRouterContext = React.createContext<unknown>(null),

  /**
   * Smart link component that automatically detects if React Router is available.
   * Uses React Router Link when in a Router context, falls back to <a> tag otherwise.
   * When inside the Puck editor, clicks are intercepted to prevent navigation.
   */
  SmartLink = React.forwardRef<
    HTMLAnchorElement,
    React.AnchorHTMLAttributes<HTMLAnchorElement> & { to?: string }
  >(({ to, href, children, onClick, ...props }, ref) => {
    const url = to || href || "#",
      handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        if (handleEditorNavigation(url)) {
          e.preventDefault();
          return;
        }
        onClick?.(e);
      },

      // Check if we're in a Router context (null default = not inside a provider)
      routerContextValue = useContext(
        (RouterContext ?? NoRouterContext) as React.Context<unknown>,
      ),
      isInRouter = RouterContext != null && routerContextValue != null,
      // A spot on this page is the browser's job: a router Link turns "#contact"
      // Into "/#contact", and a client-side navigation to that never scrolls.
      isSamePageAnchor = url.startsWith("#");

    // Use Link if we have it and we're in a Router
    if (isInRouter && RouterLink && !isSamePageAnchor) {
      return (
        <RouterLink to={url} {...props} ref={ref} onClick={handleClick}>
          {children}
        </RouterLink>
      );
    }

    // Fallback to regular anchor tag
    return (
      <a href={url} {...props} ref={ref} onClick={handleClick}>
        {children}
      </a>
    );
  });

SmartLink.displayName = "SmartLink";

export { SmartLink };
